import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./clinic-message-window.css";

type Bounds = { left: number; top: number; width: number; height: number };
type Adjustment = "move" | "resize";
type PointerAdjustment = {
  mode: Adjustment;
  pointerId: number;
  target: HTMLElement;
  clientX: number;
  clientY: number;
  bounds: Bounds;
};

function constrain(bounds: Bounds): Bounds {
  const margin = window.innerWidth < 680 ? 8 : 12;
  const availableWidth = Math.max(1, window.innerWidth - margin * 2);
  const availableHeight = Math.max(1, window.innerHeight - margin * 2);
  const width = Math.min(availableWidth, Math.max(Math.min(380, availableWidth), bounds.width));
  const height = Math.min(availableHeight, Math.max(Math.min(420, availableHeight), bounds.height));
  return {
    width,
    height,
    left: Math.max(margin, Math.min(bounds.left, window.innerWidth - width - margin)),
    top: Math.max(margin, Math.min(bounds.top, window.innerHeight - height - margin)),
  };
}

function initialBounds(): Bounds {
  const mobile = window.innerWidth < 680;
  const width = mobile ? window.innerWidth - 16 : 620;
  return constrain({
    left: mobile ? 8 : window.innerWidth - width - 24,
    top: mobile ? 8 : 84,
    width,
    height: mobile ? window.innerHeight - 16 : Math.min(720, window.innerHeight - 108),
  });
}

function adjusted(bounds: Bounds, mode: Adjustment, x: number, y: number): Bounds {
  if (mode === "move") return constrain({ ...bounds, left: bounds.left + x, top: bounds.top + y });
  const margin = window.innerWidth < 680 ? 8 : 12;
  // Resizing from the corner keeps the opposite corner anchored, including at viewport edges.
  return constrain({
    ...bounds,
    width: Math.min(bounds.width + x, window.innerWidth - bounds.left - margin),
    height: Math.min(bounds.height + y, window.innerHeight - bounds.top - margin),
  });
}

/** A non-modal message window: page navigation and draft fields remain usable together. */
export function ClinicMessageWindow({
  title,
  eyebrow,
  children,
  actions,
  className = "",
  onClose,
}: {
  title: string;
  eyebrow?: string;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
  onClose(): void;
}) {
  const titleId = useId();
  const instructionsId = useId();
  const windowRef = useRef<HTMLElement>(null);
  const pointer = useRef<PointerAdjustment | null>(null);
  const [bounds, setBounds] = useState(initialBounds);

  const finishAdjustment = useCallback(() => {
    const active = pointer.current;
    pointer.current = null;
    if (active?.target.hasPointerCapture?.(active.pointerId)) {
      active.target.releasePointerCapture(active.pointerId);
    }
  }, []);

  useEffect(() => {
    const opener = document.activeElement;
    const element = windowRef.current;
    if (element && !element.contains(document.activeElement))
      element.focus({ preventScroll: true });
    const resize = () => {
      finishAdjustment();
      setBounds((current) => constrain(current));
    };
    window.addEventListener("resize", resize);
    window.addEventListener("blur", finishAdjustment);
    return () => {
      finishAdjustment();
      window.removeEventListener("resize", resize);
      window.removeEventListener("blur", finishAdjustment);
      // Preserve focus if the user moved on to something in the page behind the window.
      if (
        opener instanceof HTMLElement &&
        opener.isConnected &&
        (element?.contains(document.activeElement) || document.activeElement === document.body)
      ) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [finishAdjustment]);

  function beginAdjustment(event: PointerEvent<HTMLElement>, mode: Adjustment) {
    if (event.button !== 0 || event.isPrimary === false || pointer.current) return;
    if (mode === "move" && event.target instanceof Element) {
      const control = event.target.closest("button, a, input, textarea, select, [contenteditable]");
      if (control && !control.hasAttribute("data-window-move")) return;
    }
    event.preventDefault();
    pointer.current = {
      mode,
      pointerId: event.pointerId,
      target: event.currentTarget,
      clientX: event.clientX,
      clientY: event.clientY,
      bounds,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function continueAdjustment(event: PointerEvent<HTMLElement>) {
    const active = pointer.current;
    if (!active || active.pointerId !== event.pointerId) return;
    if (event.buttons === 0) {
      finishAdjustment();
      return;
    }
    setBounds(
      adjusted(
        active.bounds,
        active.mode,
        event.clientX - active.clientX,
        event.clientY - active.clientY,
      ),
    );
  }

  function finishPointerAdjustment(event: PointerEvent<HTMLElement>) {
    if (pointer.current?.pointerId === event.pointerId) finishAdjustment();
  }

  function adjustWithKeyboard(event: KeyboardEvent<HTMLButtonElement>, mode: Adjustment) {
    const step = event.shiftKey ? 40 : 10;
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const change = delta[event.key];
    if (!change) return;
    event.preventDefault();
    event.stopPropagation();
    setBounds((current) => adjusted(current, mode, ...change));
  }

  return createPortal(
    <section
      ref={windowRef}
      className={`cm-window ${className}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      tabIndex={-1}
      style={bounds}
      onKeyDown={(event) => {
        if (event.key === "Escape" && !event.defaultPrevented) {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <header
        className="cmw-header"
        onPointerDown={(event) => beginAdjustment(event, "move")}
        onPointerMove={continueAdjustment}
        onPointerUp={finishPointerAdjustment}
        onPointerCancel={finishPointerAdjustment}
        onLostPointerCapture={finishPointerAdjustment}
      >
        <button
          type="button"
          className="cmw-icon-button cmw-move"
          data-window-move
          aria-label="Move message window"
          aria-describedby={instructionsId}
          title="Drag to move · Arrow keys move when focused"
          onKeyDown={(event) => adjustWithKeyboard(event, "move")}
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="M10 2v16M2 10h16M7 5l3-3 3 3M7 15l3 3 3-3M5 7l-3 3 3 3M15 7l3 3-3 3" />
          </svg>
        </button>
        <div className="cmw-heading">
          {eyebrow ? <p className="cmw-eyebrow">{eyebrow}</p> : null}
          <h2 id={titleId}>{title}</h2>
        </div>
        <button
          type="button"
          className="cmw-icon-button cmw-close"
          aria-label={`Close ${title}`}
          onClick={onClose}
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="m5 5 10 10M15 5 5 15" />
          </svg>
        </button>
      </header>
      <p id={instructionsId} className="cmw-visually-hidden">
        Use arrow keys to adjust this window. Hold Shift for larger steps.
      </p>
      <div className="cmw-body">{children}</div>
      {actions ? <footer className="cmw-footer">{actions}</footer> : null}
      <button
        type="button"
        className="cmw-resize"
        aria-label="Resize message window"
        aria-describedby={instructionsId}
        title="Drag to resize · Arrow keys resize when focused"
        onPointerDown={(event) => beginAdjustment(event, "resize")}
        onPointerMove={continueAdjustment}
        onPointerUp={finishPointerAdjustment}
        onPointerCancel={finishPointerAdjustment}
        onLostPointerCapture={finishPointerAdjustment}
        onKeyDown={(event) => adjustWithKeyboard(event, "resize")}
      >
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
          <path d="m5 15 10-10M10 15l5-5" />
        </svg>
      </button>
    </section>,
    document.body,
  );
}
